// Schéma des formulaires, source unique pour :
// - les pages (affichage des champs, src/layout.js → schemaForm) ;
// - le navigateur (contrôle, choix des jours, récapitulatif : js/site.js) ;
// - le serveur (validation, e-mails, CRM : server/leads.js).
//
// Types de champ : text, email, tel, number, select, choice (boutons radio),
// days (jours d'ouverture à cocher), textarea, checkbox.
// `options: "models"` = la gamme de src/data/models.js.

import { FAMILIES } from "./lineup.js";

const IDENTITY = [
  { name: "prenom", label: "Prénom", required: true, autocomplete: "given-name" },
  { name: "nom", label: "Nom", required: true, autocomplete: "family-name" },
  { name: "email", label: "E-mail", type: "email", required: true, autocomplete: "email" },
  { name: "telephone", label: "Téléphone", type: "tel", required: true, autocomplete: "tel", hint: "Pour vous rappeler." },
];

export const FORMS = {
  essai: {
    title: "Demande d'essai",
    ack: "votre demande d'essai est bien reçue", // objet de l'accusé de réception
    submit: "Demander mon essai",
    // Phrase affichée après l'envoi et reprise dans l'accusé de réception.
    next: "Nous vous recontactons pour confirmer le jour et l'heure de votre essai.",
    subject: (d) => `Essai ${d.modele}`,
    fields: [
      { name: "modele", label: "Modèle à essayer", type: "select", options: "models", required: true, wide: true },
      { name: "jours", label: "Jours qui vous conviennent", type: "days", schedule: "shop", max: 3, wide: true, hint: "Jusqu'à trois jours d'ouverture du magasin." },
      { name: "creneau", label: "Moment de la journée", type: "choice", options: ["Matin", "Après-midi", "Peu importe"], wide: true },
      ...IDENTITY,
      { name: "permis", label: "Votre permis", type: "select", options: ["Permis A", "Permis A2", "Permis en cours"], wide: true },
      { name: "message", label: "Message", type: "textarea", rows: 3, wide: true },
    ],
  },

  atelier: {
    title: "Demande de rendez-vous atelier",
    ack: "votre demande de rendez-vous est bien reçue",
    submit: "Demander un rendez-vous",
    next: "La date et l'horaire de votre rendez-vous ne sont confirmés qu'après validation de notre part : nous vous recontactons.",
    subject: (d) => `Atelier : ${d.intervention} · ${d.moto}`,
    fields: [
      { name: "moto", label: "Votre moto", required: true, placeholder: "Modèle et année, ex. Street Glide 2021", wide: true },
      { name: "immatriculation", label: "Immatriculation", placeholder: "AB-123-CD", hint: "Pour retrouver l'historique de votre moto." },
      { name: "kilometrage", label: "Kilométrage", type: "number", min: 0, max: 500000 },
      { name: "intervention", label: "Type d'intervention", type: "select", required: true, wide: true, options: ["Révision", "Montage d'accessoires", "Pneumatiques", "Panne ou réparation", "Customisation", "Autre"] },
      { name: "jours", label: "Jours qui vous conviennent", type: "days", schedule: "workshop", max: 3, required: true, wide: true, hint: "Jusqu'à trois jours d'ouverture de l'atelier." },
      ...IDENTITY,
      { name: "message", label: "Précisions", type: "textarea", rows: 3, wide: true },
    ],
  },

  reprise: {
    title: "Demande d'estimation de reprise",
    ack: "votre demande d'estimation est bien reçue",
    submit: "Demander une estimation",
    next: "Nous vous recontactons pour l'estimation. L'estimation définitive se fait après examen de la moto à la concession.",
    subject: (d) => `Reprise : ${d.marque} ${d.modele_actuel} (${d.annee})`,
    fields: [
      { name: "projet", label: "Votre projet", type: "select", required: true, wide: true, options: ["Reprise pour l'achat d'une moto neuve", "Reprise pour l'achat d'une occasion", "Dépôt-vente", "Je ne sais pas encore"] },
      { name: "marque", label: "Marque", required: true, placeholder: "Harley-Davidson, Triumph…" },
      { name: "modele_actuel", label: "Modèle", required: true },
      { name: "annee", label: "Année de mise en circulation", type: "number", required: true, min: 1950, max: 2100 },
      { name: "kilometrage", label: "Kilométrage", type: "number", required: true, min: 0, max: 500000 },
      ...IDENTITY,
      { name: "message", label: "État, options, entretien…", type: "textarea", rows: 3, wide: true },
    ],
  },

  contact: {
    title: "Message",
    ack: "votre message est bien reçu",
    submit: "Envoyer",
    next: "Votre message est bien arrivé à la concession.",
    subject: (d) => `Contact : ${d.objet}`,
    fields: [
      { name: "objet", label: "Objet", type: "select", required: true, options: ["Demande d'information", "Demande de documentation", "Moto neuve", "Occasion", "Atelier", "Équipement et accessoires", "Autre"] },
      { name: "interet", label: "Famille de moto", type: "select", options: [...FAMILIES.map((f) => f.label), "Autre"] },
      ...IDENTITY,
      { name: "message", label: "Message", type: "textarea", required: true, rows: 5, wide: true },
      { name: "newsletter", label: "Je souhaite recevoir les actualités et événements de la concession par e-mail.", type: "checkbox", wide: true },
    ],
  },

  newsletter: {
    title: "Inscription aux actualités",
    ack: "votre inscription est bien prise en compte",
    submit: "M'inscrire",
    next: "C'est noté : vous recevrez les prochaines actualités de la concession.",
    subject: () => "Inscription aux actualités",
    fields: [
      { name: "email", label: "E-mail", type: "email", required: true, autocomplete: "email", wide: true },
      { name: "consentement", label: "J'accepte de recevoir par e-mail les actualités et événements de la concession. Je peux me désinscrire à tout moment.", type: "checkbox", required: true, wide: true },
    ],
  },
};

// Champs non saisis par le visiteur, ajoutés par le navigateur.
export const META_FIELDS = ["_type", "_page", "_t", "site_web"];
